import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import { useAuthState } from "../../Context";
import MediaCard from "./MediaCard";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import MainLayout from "../Common/MainLayout";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

export default function Dashboard() {
  const { session } = useAuthState();

  const fetchDashboardData = async () => {
    // TODO: no need to save session in state, it can be retrieved from user.getSession.
    const token = session.getIdToken().getJwtToken();
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/getDashboard`,
      {
        headers: {
          authorizationToken: token,
        },
      }
    );
    // simulate error
    // throw new Error("Failed to fetch data");
    return response.data;
  };

  const {
    data: dashboardData,
    error,
    isLoading,
    isFetching,
    isFetched,
    isStale,
    dataUpdatedAt,
    isSuccess,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: ["dashboardData", session.getIdToken().getJwtToken()],
    queryFn: fetchDashboardData,
    staleTime: 60 * 60 * 1000, // 60 minutes
    cacheTime: 2 * 60 * 60 * 1000, // 120 minutes
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    console.log("Component render:", {
      isLoading,
      isFetching,
      isFetched,
      isStale,
      dataUpdatedAt,
      isSuccess,
      isFetchedAfterMount,
    });
  }, [
    isLoading,
    isFetching,
    isFetched,
    isStale,
    isSuccess,
    isFetchedAfterMount,
    dashboardData,
    dataUpdatedAt,
  ]);

  const groupedData = useMemo(() => {
    if (!dashboardData) return {};

    const unsortedGroupedData = dashboardData.reduce((acc, item) => {
      const groupKey = item.tile_group_name || "Uncategorized";
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    }, {});

    // Sort the keys of the grouped data
    const sortedKeys = Object.keys(unsortedGroupedData).sort();

    // Create a new object with sorted keys
    const sortedGroupedData = {};
    sortedKeys.forEach(key => {
      sortedGroupedData[key] = unsortedGroupedData[key];
    });
    console.log("Grouped Data:", sortedGroupedData); // Log the grouped data
    return sortedGroupedData;
  }, [dashboardData]);


  return (
    <MainLayout>
      {isLoading && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "100%" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading Dashboard...
            </Typography>
          </div>
        </Grid>
      )}
      {error && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "100%" }}
        >
          <Alert severity="error">
            {error.response
              ? error.response.data.message
              : "Failed to fetch data"}
          </Alert>
        </Grid>
      )}
      {!isLoading && !error && groupedData && (
        <Grid
          container
          spacing={2}
          sx={{
            mt: 4,
            ml: 4,
            mr: 4,
            mb: 4,
            overflowY: "scroll",
          }}
          alignItems={Object.keys(groupedData).length === 1 ? "flex-start" : "stretch"}  // Conditional alignment
        >
          {Object.entries(groupedData).map(([workbookUrl, items], index) => (
            <Grid container item spacing={3} key={index} sx={{ width: "100%" }}>
              <React.Fragment key={index}>
                <Grid item xs={12}  >
                  <Typography variant="h6" gutterBottom>
                    {workbookUrl}
                  </Typography>
                  <Divider />
                </Grid>
                {items[0].tile_group_description && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <i>{items[0].tile_group_description}</i>
                    </Typography>
                  </Grid>
                )}
                <Grid container item spacing={3} sx={{ mb: 6 }}>
                  {items.map((item, idx) => (
                    <Grid item xs={12} md={4} lg={3} key={idx}>
                      <MediaCard data={item} />
                    </Grid>
                  ))}
                </Grid>
              </React.Fragment>
            </Grid>
          ))}
        </Grid>
      )}
    </MainLayout>
  );
}
